import {
  useGetResourceLabel,
  useListContextWithProps,
  useResourceContext,
  useResourceDefinition,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { CreateButton } from "@/components/admin/create-button";
import {
  ListEmptyState,
  ListErrorState,
} from "@/components/atomic-crm/design/ListStates";

/**
 * Default empty state for a mobile SimpleList. Distinguishes between three
 * situations: the request failed (error + "Reintentar"), the current
 * filters/search matched nothing (filtered message + "clear filters"), or
 * the resource truly has zero rows (its own empty title, with an optional
 * create action).
 */
export const ListNoResults = (props: ListNoResultsProps) => {
  const translate = useTranslate();
  const resource = useResourceContext(props);
  const { error, filterValues, setFilters, refetch } =
    useListContextWithProps(props);
  const getResourceLabel = useGetResourceLabel();
  const { hasCreate } = useResourceDefinition({ resource });
  if (!resource) {
    throw new Error("<ListNoResults> must be used inside a <List> component");
  }

  if (error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetch?.()}
      />
    );
  }

  if (filterValues && setFilters && Object.keys(filterValues).length > 0) {
    return (
      <ListEmptyState
        title={translate("ra.navigation.no_filtered_results", {
          resource,
          name: getResourceLabel(resource, 0),
          _: "No results found with the current filters.",
        })}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilters({}, [])}
          >
            {translate("ra.navigation.clear_filters", {
              _: "Clear filters",
            })}
          </Button>
        }
      />
    );
  }

  const resourceName = translate(`resources.${resource}.forcedCaseName`, {
    smart_count: 0,
    _: getResourceLabel(resource, 0),
  });

  return (
    <ListEmptyState
      title={translate(`resources.${resource}.empty.title`, {
        _: translate("ra.page.empty", { name: resourceName }),
      })}
      description={translate(`resources.${resource}.empty.description`, {
        _: translate("ra.page.invite"),
      })}
      action={hasCreate ? <CreateButton /> : undefined}
    />
  );
};

export interface ListNoResultsProps {
  resource?: string;
  filterValues?: any;
  setFilters?: (filters: any, filterTypes?: string[]) => void;
}
